import { Inject, Injectable } from '@angular/core';
import extend from 'lodash-es/extend';
import find from 'lodash-es/find';

import {
  IFBIdentityProvider,
  IFBIdpType,
  IFBSamlProviderInformation,
  IFBSsoIdentityProvider,
  IFBSsoStatus
} from '@app/models/sso.model';
import { IFBWindow } from '@app/models/window-namespace';
import { SamlResource } from '@app/shared/modules/resources/saml.resource';
import { TenantResource } from '@app/shared/modules/resources/tenant.resource';
import { of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class SsoService {
  identityProviders: IFBIdentityProvider[] = [];
  ssoIdentityProvider: IFBSsoIdentityProvider = {} as IFBSsoIdentityProvider;
  usernamePasswordIdentityProvider?: IFBIdentityProvider;
  samlProviderInformation: IFBSamlProviderInformation = {} as IFBSamlProviderInformation;

  private configured: boolean = false;
  private visible: boolean = false;
  private ssoOnly: boolean = false;

  constructor(
    @Inject('Window') private window: IFBWindow,
    private samlResource: SamlResource,
    private tenantResource: TenantResource
  ) {}

  get isConfigured(): boolean {
    return this.configured;
  }

  get isEnabled(): boolean {
    return this.isConfigured && this.ssoIdentityProvider.status === 'ENABLED';
  }

  // The idea behind checking whether SSO Only is enabled or not it that
  // 'SAML2' type IDP should be ENABLED and all other identity providers should be DISABLED
  get isSsoOnlyEnabled(): boolean {
    return this.ssoOnly;
  }

  get isVisible(): boolean {
    return this.visible;
  }

  getIdpByType(type: IFBIdpType): IFBIdentityProvider | null {
    return find(this.identityProviders, { type });
  }

  toggle() {
    if (!this.isConfigured) {
      return of(false);
    }

    const newStatus = { status: this.isEnabled ? 'DISABLED' : 'ENABLED' } as IFBSsoStatus;

    return this.tenantResource.changeIdentityProviderStatus(this.ssoIdentityProvider.id, newStatus).pipe(
      map(() => {
        extend(this.ssoIdentityProvider, newStatus);
        return this.isEnabled;
      })
    );
  }

  toggleSsoOnly() {
    // If SSO is disabled then exit with false
    if (!this.isEnabled) {
      return of(false);
    }

    const newStatus = { status: this.isSsoOnlyEnabled ? 'ENABLED' : 'DISABLED' } as IFBSsoStatus;

    return this.tenantResource.changeIdentityProviderStatus(this.usernamePasswordIdentityProvider.id, newStatus).pipe(
      map(saml => {
        extend(this.usernamePasswordIdentityProvider, newStatus);

        this.ssoOnly = this.usernamePasswordIdentityProvider.status === 'DISABLED';

        return this.isEnabled;
      })
    );
  }

  setIdentityProviders(items: IFBIdentityProvider[]): void {
    this.identityProviders = items;
    this.ssoIdentityProvider = this.getIdpByType('SAML2') as IFBSsoIdentityProvider;
    this.usernamePasswordIdentityProvider = this.getIdpByType('USERNAME_PASSWORD') as IFBIdentityProvider;

    if (this.ssoIdentityProvider) {
      this.configured = true;
    }

    const enabled = items.filter(idp => idp.status === 'ENABLED');
    this.ssoOnly = enabled.length === 1 && enabled[0].type === 'SAML2';
  }

  fetchIdentityProviders() {
    return this.tenantResource.getIdentityProviders().pipe(
      map(response => {
        this.setIdentityProviders(response.items);
        return response;
      })
    );
  }

  fetchSamlProviderInformation() {
    return this.samlResource.getMetadata().pipe(
      map(samlProviderInformation => {
        this.samlProviderInformation = samlProviderInformation;
        return samlProviderInformation;
      })
    );
  }

  fetchSsoStatus() {
    return this.tenantResource.getDetails().pipe(
      map(tenant => {
        this.visible = Boolean(find(tenant.capabilities.items, { type: 'SAML2' }));
        return this.visible;
      }),
      catchError(() => {
        this.visible = false;
        return of(false);
      })
    );
  }

  saveConfiguration(samlConfiguration: IFBSsoIdentityProvider) {
    let request$;

    if (this.isConfigured) {
      request$ = this.tenantResource.saveIdentityProvider({
        id: this.ssoIdentityProvider.id,
        entity_id: samlConfiguration.entity_id,
        sso_service_location: samlConfiguration.sso_service_location,
        signing_certificate: samlConfiguration.signing_certificate,
        type: 'SAML2'
      });
    } else {
      request$ = this.tenantResource.createIdentityProvider({
        entity_id: samlConfiguration.entity_id,
        sso_service_location: samlConfiguration.sso_service_location,
        signing_certificate: samlConfiguration.signing_certificate,
        type: 'SAML2'
      });
    }

    return request$.pipe(
      map(ssoIdentityProvider => {
        extend(this.ssoIdentityProvider, ssoIdentityProvider);
        this.configured = true;
        return this.ssoIdentityProvider;
      })
    );
  }

  setSsoOnly(value: boolean) {
    this.ssoOnly = value;
  }
}
